import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ConceptMapData } from '../types/wordUsage';
import { Loader2, RefreshCw, Flame } from 'lucide-react';
import * as d3 from 'd3';
import { useTheme } from 'next-themes';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';

interface ConceptMapVisualizationProps {
  data: ConceptMapData;
  loading: boolean;
  width?: number;
  height?: number;
}

const ConceptMapVisualization: React.FC<ConceptMapVisualizationProps> = ({
  data,
  loading,
  width = 800,
  height = 600,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  // State to track if visualization has been initialized
  const [, setInitialized] = useState(false);
  // Get current theme
  const { resolvedTheme: theme } = useTheme();

  // Force simulation reference
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null);

  // Default force parameters using standard D3 naming
  const defaultForceParams = {
    linkDistance: 50, // Distance between connected nodes
    charge: -300, // Repulsion between all nodes (negative = repulsion)
    collideRadius: 20, // Collision detection radius
  };

  // Force parameters state
  const [forceParams, setForceParams] = useState(defaultForceParams);

  // UI state for controls visibility
  const [showControls, setShowControls] = useState(false);

  // Update force simulation when parameters change - using standard D3 forces
  const updateForceSimulation = useCallback(() => {
    if (!simulationRef.current) return;

    // Calculate node radius function for collision detection
    const getNodeRadius = (d: any) => Math.max(8, Math.min(25, 8 + Math.sqrt(d.count || 1)));

    simulationRef.current
      // Link force - keeps connected nodes at specified distance
      .force(
        'link',
        d3
          .forceLink()
          .id((d: any) => d.id)
          .distance(forceParams.linkDistance)
          .strength(0.3) // Lower strength allows more flexibility
      )
      // Charge force - repulsion between all nodes
      .force('charge', d3.forceManyBody().strength(forceParams.charge))
      // Collision detection - prevents nodes from overlapping
      .force('collide', d3.forceCollide().radius(forceParams.collideRadius).strength(0.7))
      // Center force - keeps the graph centered in the viewport
      .force('center', d3.forceCenter(width / 2, height / 2))
      // Reheat the simulation
      .alpha(0.3)
      .restart();
  }, [forceParams, width, height]);

  // Reset force parameters to defaults
  const resetForceParams = useCallback(() => {
    setForceParams(defaultForceParams);
  }, []);

  // Add a resize handler to update the visualization when the window size changes
  useEffect(() => {
    const handleResize = () => {
      setInitialized(false); // Trigger a redraw

      // Update width and height props based on new window size
      if (svgRef.current) {
        const newWidth = window.innerWidth - 340 - 48; // Adjust for sidebar and padding (24px on each side) and internal padding (24px on each side)
        const newHeight = window.innerHeight - 228 - 48 - 24; // Adjust for header, filter bar, padding, and bottom margin (24px)

        svgRef.current.setAttribute('width', newWidth.toString());
        svgRef.current.setAttribute('height', newHeight.toString());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (loading || !data.nodes.length || !svgRef.current) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Determine text color based on theme
    const textColor = theme === 'dark' ? '#ffffff' : '#000000';
    const textStrokeColor = theme === 'dark' ? '#000000' : '#ffffff';

    // Calculate node radius function for collision detection
    const getNodeRadius = (d: any) => Math.max(8, Math.min(25, 8 + Math.sqrt(d.count || 1)));

    // Create the force simulation with standard D3 forces
    const simulation = d3
      .forceSimulation(data.nodes as d3.SimulationNodeDatum[])
      // Link force - keeps connected nodes at specified distance
      .force(
        'link',
        d3
          .forceLink(data.edges as d3.SimulationLinkDatum<d3.SimulationNodeDatum>[])
          .id((d: any) => d.id)
          .distance(forceParams.linkDistance)
          .strength(0.3) // Lower strength allows more flexibility
      )
      // Charge force - repulsion between all nodes
      .force('charge', d3.forceManyBody().strength(forceParams.charge))
      // Collision detection - prevents nodes from overlapping using dynamic radius
      .force(
        'collide',
        d3
          .forceCollide()
          .radius((d: any) => {
            // Use the same radius calculation as the circles with padding
            const baseSize = 8; // Minimum size for nodes with 1 connection
            const sizePerConnection = 3; // Size increase per connection
            const connectionCount = Math.max(1, d.connectionCount || 1); // Ensure minimum of 1
            const radius = baseSize + (connectionCount - 1) * sizePerConnection;
            return radius + 6; // Add padding to prevent overlap
          })
          .strength(0.8)
      )
      // Center force - keeps the graph centered in the viewport
      .force('center', d3.forceCenter(width / 2, height / 2));

    // Store simulation reference for controls
    simulationRef.current = simulation;

    // Create the SVG container with equal padding on all sides
    const padding = 24; // Equal padding on all sides
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [-padding, -padding, width + padding * 2, height + padding * 2])
      .attr('style', 'max-width: 100%; height: 100%;');

    // Add zoom behavior
    const zoom = d3.zoom().on('zoom', (event) => {
      g.attr('transform', event.transform);
    });

    svg.call(zoom as any);

    // Create a group for all elements with initial transform to center content
    const g = svg.append('g').attr('transform', `translate(${padding}, ${padding})`);

    // We'll use the node's own color property instead of a color scale

    // Determine edge color based on theme
    const edgeColor = theme === 'dark' ? '#888' : '#555';

    // Create the links with much thinner, cleaner styling
    const link = g
      .append('g')
      .selectAll('line')
      .data(data.edges)
      .join('line')
      .attr('stroke', edgeColor) // Theme-based edge color
      .attr('stroke-opacity', theme === 'dark' ? 0.3 : 0.2) // Much lower opacity for cleaner look
      .attr('stroke-width', 0.5) // Very thin lines for minimal visual clutter
      .attr('stroke-linecap', 'round'); // Rounded ends

    // Create the nodes
    const node = g
      .append('g')
      .selectAll('.node')
      .data(data.nodes)
      .join('g')
      .attr('class', 'node')
      .call(d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended) as any);

    // Determine node stroke color based on theme
    const nodeStrokeColor = theme === 'dark' ? '#fff' : '#000';
    const nodeShadowColor =
      theme === 'dark'
        ? 'drop-shadow(0 0 2px rgba(255,255,255,0.3))'
        : 'drop-shadow(0 0 2px rgba(0,0,0,0.3))';

    // Add circles to nodes with bubble chart styling
    node
      .append('circle')
      .attr('r', (d: any) => {
        // Size directly proportional to connection count
        const baseSize = 8; // Minimum size for nodes with 1 connection
        const sizePerConnection = 3; // Size increase per connection
        const connectionCount = Math.max(1, d.connectionCount || 1); // Ensure minimum of 1
        return baseSize + (connectionCount - 1) * sizePerConnection;
      })
      .attr('fill', (d: any) => {
        // Simplified color scheme with better contrast
        if (d.forgeTypes.includes('Item')) return '#22c55e'; // Green
        if (d.forgeTypes.includes('Location')) return '#3b82f6'; // Blue
        if (d.forgeTypes.includes('Character')) return '#ef4444'; // Red
        if (d.forgeTypes.includes('Deconstructor')) return '#f59e0b'; // Orange
        return '#6b7280'; // Gray for mixed/unknown
      })
      .attr('stroke', theme === 'dark' ? '#ffffff' : '#000000') // High contrast stroke
      .attr('stroke-width', 0.8) // Thinner border
      .attr('stroke-opacity', 0.4) // Lower opacity stroke
      .attr('fill-opacity', 0.8); // Slightly transparent fill

    // Add improved labels to nodes
    node
      .append('text')
      .attr('dx', 14) // Move text further from node
      .attr('dy', '.35em')
      .text((d: any) => d.label)
      .attr('font-size', '12px') // Larger font
      .attr('font-weight', 'bold') // Bold text
      .attr('fill', textColor) // Theme-based text color
      .attr('stroke', textStrokeColor) // Theme-based outline
      .attr('stroke-width', '0.5px') // Thin outline
      .attr('stroke-opacity', 0.7) // Semi-transparent outline
      .attr('paint-order', 'stroke'); // Draw stroke behind fill

    // Add enhanced tooltips with more information
    node.append('title').text((d: any) => {
      // Show connection count and usage information
      const forgeTypes = Array.isArray(d.forgeTypes) ? d.forgeTypes.join(', ') : 'Unknown';

      return `${d.label}\nConnections: ${d.connectionCount || 0}\nUsage Count: ${
        d.count
      }\nForge Types: ${forgeTypes}`;
    });

    // Update positions on each tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Standard D3 drag functions
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);

      // Release the node when dragging ends
      // This allows the force simulation to continue moving nodes
      d.fx = null;
      d.fy = null;
    }

    setInitialized(true);

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [data, loading, width, height, theme]);

  // Effect to update simulation when force parameters change
  useEffect(() => {
    if (simulationRef.current) {
      updateForceSimulation();
    }
  }, [forceParams, updateForceSimulation]);

  return (
    <div className="w-full h-full p-6 pb-12">
      {loading ? (
        <div className="flex items-center justify-center h-full w-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !data.nodes.length ? (
        <div className="flex items-center justify-center h-full w-full">
          <p className="text-muted-foreground">No data available</p>
        </div>
      ) : (
        <div className="relative w-full h-full overflow-hidden">
          <svg ref={svgRef} className="w-full h-full" />

          {/* Toggle Button for Force Controls */}
          <Button
            variant="outline"
            size="sm"
            className="absolute top-6 left-6 z-10"
            onClick={() => setShowControls(!showControls)}
          >
            {showControls ? 'Hide Controls' : 'Show Force Controls'}
          </Button>

          {/* Force Controls Panel */}
          {showControls && (
            <div className="absolute top-16 left-6 bg-background/90 p-3 rounded-md border shadow-md max-w-[300px] z-10">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium">Force Controls</h3>
                <div className="flex gap-1">
                  {/* Reheat button to make the simulation more active */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => {
                      if (simulationRef.current) {
                        // Reheat the simulation to make it more active
                        simulationRef.current.alpha(0.8).restart();
                      }
                    }}
                    title="Reheat simulation"
                  >
                    <Flame className="h-4 w-4 text-orange-500" />
                  </Button>

                  {/* Reset button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={resetForceParams}
                    title="Reset to defaults"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {/* Link Distance Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <label>Link Distance</label>
                    <span className="text-muted-foreground">{forceParams.linkDistance}</span>
                  </div>
                  <Slider
                    value={[forceParams.linkDistance]}
                    min={10}
                    max={200}
                    step={5}
                    onValueChange={(values) => {
                      setForceParams((prev) => ({ ...prev, linkDistance: values[0] }));
                      // Apply changes immediately for better responsiveness
                      if (simulationRef.current) {
                        const linkForce = simulationRef.current.force('link') as d3.ForceLink<
                          d3.SimulationNodeDatum,
                          d3.SimulationLinkDatum<d3.SimulationNodeDatum>
                        >;
                        if (linkForce) {
                          linkForce.distance(values[0]);
                          simulationRef.current.alpha(0.3).restart();
                        }
                      }
                    }}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Target distance between connected nodes
                  </div>
                </div>

                {/* Charge Strength Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <label>Charge Strength</label>
                    <span className="text-muted-foreground">{forceParams.charge}</span>
                  </div>
                  <Slider
                    value={[-forceParams.charge]} // Invert for UI (positive = stronger repulsion)
                    min={0}
                    max={1000}
                    step={50}
                    onValueChange={(values) => {
                      const newCharge = -values[0]; // Convert back to negative
                      setForceParams((prev) => ({ ...prev, charge: newCharge }));
                      // Apply changes immediately for better responsiveness
                      if (simulationRef.current) {
                        const chargeForce = simulationRef.current.force(
                          'charge'
                        ) as d3.ForceManyBody<d3.SimulationNodeDatum>;
                        if (chargeForce) {
                          chargeForce.strength(newCharge);
                          simulationRef.current.alpha(0.3).restart();
                        }
                      }
                    }}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Repulsion between all nodes (higher = more spread out)
                  </div>
                </div>

                {/* Collision Radius Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <label>Collision Radius</label>
                    <span className="text-muted-foreground">{forceParams.collideRadius}</span>
                  </div>
                  <Slider
                    value={[forceParams.collideRadius]}
                    min={5}
                    max={50}
                    step={1}
                    onValueChange={(values) => {
                      setForceParams((prev) => ({ ...prev, collideRadius: values[0] }));
                      // Apply changes immediately for better responsiveness
                      if (simulationRef.current) {
                        const collideForce = simulationRef.current.force(
                          'collide'
                        ) as d3.ForceCollide<d3.SimulationNodeDatum>;
                        if (collideForce) {
                          collideForce.radius(values[0]);
                          simulationRef.current.alpha(0.3).restart();
                        }
                      }
                    }}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Minimum distance between any nodes
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Forge Type Legend */}
          <div className="absolute bottom-6 right-6 bg-background/90 p-2 rounded-md border shadow-md">
            <div className="flex flex-col gap-2 text-xs">
              <div className="text-center font-medium mb-1">Forge Types</div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Items</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span>Locations</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Characters</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span>Deconstructor</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span>Mixed/Unknown</span>
              </div>
              <div className="text-center text-xs text-muted-foreground mt-1">
                Size = Connection Count • Hover for details
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConceptMapVisualization;
